// client/src/theme/theme.js
import { alpha, createTheme, darken, getContrastRatio, lighten } from '@mui/material/styles';

// Central design tokens for the whole app. Keeping this in one place is what
// makes dark mode possible without hunting down hardcoded hex colors.
//
// 5 brand colors (primary/secondary/success/warning/error - the roles
// already used all over the UI as Button/Chip/Alert colors) always have a
// value, set by an admin on the Manage Store page. backgroundColor/textColor
// are a separate, optional override - most stores leave those unset and
// just follow the light/dark mode default below like before.

const MIN_TEXT_CONTRAST = 4;

// Brand colors double as text on page backgrounds, so nudge one lighter or
// darker when it would be hard to read in the current mode.
const readable = (color, background, mode) => {
  const adjust = mode === 'dark' ? lighten : darken;
  let result = color;
  for (let step = 0; step < 12 && getContrastRatio(result, background) < MIN_TEXT_CONTRAST; step += 1) {
    result = adjust(result, 0.1);
  }
  return result;
};

export const getAppTheme = (
  mode,
  primaryColor = '#0B5FA5',
  secondaryColor = '#F26A21',
  successColor = '#22A45D',
  warningColor = '#ED6C02',
  errorColor = '#D32F2F',
  backgroundColor = null,
  textColor = null
) => {
  const isDark = mode === 'dark';
  const paper = isDark ? '#171A20' : '#FFFFFF';
  const brand = (color) => ({ main: readable(color, paper, mode) });
  const hairline = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.11)';
  const strongLine = isDark ? 'rgba(255,255,255,0.24)' : 'rgba(15,23,42,0.28)';

  return createTheme({
    palette: {
      mode,
      primary: brand(primaryColor),
      secondary: brand(secondaryColor),
      success: brand(successColor),
      warning: brand(warningColor),
      error: brand(errorColor),
      background: {
        default: backgroundColor || (isDark ? '#0E1014' : '#F4F5F7'),
        paper,
      },
      divider: hairline,
      text: {
        primary: textColor || (isDark ? '#F2F4F7' : '#12151C'),
        secondary: isDark ? '#A3ACB9' : '#5B6472',
      },
    },
    shape: {
      borderRadius: 10,
    },
    typography: {
      fontFamily: [
        'Inter',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 800, letterSpacing: '-0.03em' },
      h2: { fontWeight: 800, letterSpacing: '-0.03em' },
      h3: { fontWeight: 800, letterSpacing: '-0.025em' },
      h4: { fontWeight: 800, letterSpacing: '-0.02em', fontSize: '1.875rem' },
      h5: { fontWeight: 800, letterSpacing: '-0.02em', fontSize: '1.5rem' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em', fontSize: '1.125rem' },
      subtitle1: { fontWeight: 600, lineHeight: 1.35 },
      body1: { lineHeight: 1.6 },
      overline: { fontWeight: 700, letterSpacing: '0.12em' },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.005em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: (theme) => ({
          '::selection': { backgroundColor: alpha(theme.palette.primary.main, 0.25) },
          // Plain links would otherwise use the browser's blue, unreadable in dark mode.
          'a:not([class])': { color: theme.palette.primary.main },
          'a:focus-visible, button:focus-visible': {
            outline: `2px solid ${theme.palette.primary.main}`,
            outlineOffset: 2,
          },
          body: { scrollbarColor: `${strongLine} transparent` },
        }),
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: 999, paddingInline: 20 },
          sizeLarge: { paddingBlock: 10, fontSize: '0.95rem' },
          outlined: { borderColor: strongLine },
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: { transition: 'background-color 0.15s ease, color 0.15s ease' },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { fontWeight: 600 },
          sizeSmall: { fontSize: '0.72rem' },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          // The default Paper reads as a flat bordered panel; floating surfaces
          // (menus, dialogs) keep their higher elevations and real shadows.
          elevation1: { boxShadow: 'none', border: `1px solid ${hairline}` },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundImage: 'none',
            backgroundColor: alpha(theme.palette.background.paper, 0.86),
            backdropFilter: 'saturate(180%) blur(14px)',
            border: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            color: theme.palette.text.primary,
          }),
        },
      },
      MuiCard: {
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: 'none',
            transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
          }),
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: ({ theme }) => ({
            backgroundColor: theme.palette.background.paper,
            '&:hover:not(.Mui-disabled):not(.Mui-focused) .MuiOutlinedInput-notchedOutline': { borderColor: strongLine },
          }),
          notchedOutline: { borderColor: hairline },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, backgroundColor: theme.palette.background.paper }),
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottomColor: hairline },
          head: ({ theme }) => ({
            fontSize: '0.72rem',
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            whiteSpace: 'nowrap',
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.action.hover,
          }),
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: { '&:last-child td': { borderBottom: 0 } },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: ({ theme }) => ({ border: `1px solid ${theme.palette.divider}`, backgroundImage: 'none' }),
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { fontWeight: 800, letterSpacing: '-0.01em' } },
      },
      MuiPopover: {
        styleOverrides: {
          paper: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            boxShadow: `0 16px 40px ${alpha('#000', isDark ? 0.5 : 0.12)}`,
          }),
        },
      },
      MuiMenuItem: {
        styleOverrides: { root: { fontSize: '0.9rem' } },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: ({ theme }) => ({
            '&.Mui-selected': {
              backgroundColor: alpha(theme.palette.primary.main, 0.12),
              color: theme.palette.primary.main,
              fontWeight: 700,
              boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}`,
            },
            '&.Mui-selected:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.18) },
          }),
        },
      },
      MuiAccordion: {
        defaultProps: { disableGutters: true, elevation: 0 },
        styleOverrides: {
          root: ({ theme }) => ({
            border: `1px solid ${theme.palette.divider}`,
            '&:not(:last-child)': { borderBottom: 0 },
            '&::before': { display: 'none' },
          }),
        },
      },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } },
      },
      MuiAlert: {
        styleOverrides: { root: { alignItems: 'center' } },
      },
      MuiTooltip: {
        defaultProps: { arrow: true },
      },
      MuiLinearProgress: {
        styleOverrides: { root: { height: 6 } },
      },
    },
  });
};
